import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import apiPaths from "../../../api/endpoints";
import { magicBackBG } from "../../appAssets";
import { useMagicBackpack } from "../../hooks/useMagicBackpack";
import MagicBagPackHeader from "./components/MagicBagPackHeader/MagicBagPackHeader";
import WallContent from "./components/WallContent/WallContent";
import RenderVideoViewer from "./components/RenderVideoViewer/RenderVideoViewer";
import RenderAudioViewer from "./components/RenderAudioViewer/RenderAudioViewer";
import {
  BackpackType,
  MagicPackItem,
  backpackApiPathFor,
  backpackTitleFor,
} from "./types";
import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";
import "./MagicBagPack.scss";

/**
 * Mirrors srcMobile/Screens/MagicBagPack/index.tsx
 *
 * Driven by ?type=video|audio|image|book (default = video, mirrors HomeRoom default).
 *
 * Flow:
 *  1. Fetch items via /retrieveMagicBackPack/<Type>
 *  2. WallContent renders featured-grid → click an item
 *  3. Open inline viewer; optimistically mark seen=true so "Watch Again" updates
 *  4. PATCH /updateMagicBackPack/<Type>/{documentId} fires after 2s (mirrors mobile)
 *  5. On viewer close, refetch list so other devices/clients stay in sync
 */
function MagicBagPack() {
  const navigate = useNavigate();
  const location = useLocation();
  const { contentType, navigatedFrom, apiUrl } = (location.state ?? {}) as {
    contentType?: string;
    navigatedFrom?: string;
    apiUrl?: string;
  };
  const [params] = useSearchParams();

  const type = (params.get("type") || "video").toLowerCase() as BackpackType;
  const isAudioView = type === "audio";
  const isAudioType = !!apiUrl?.toLowerCase().includes("audio");

  console.log("MagicBagPack nav state22", {
    isAudioType,
  });

  console.log(params.get("type"), "MagicBagPack nav state type");

  const { fetchMagicPackData, updateMagicPackSeen } = useMagicBackpack();

  const [items, setItems] = useState<MagicPackItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeItem, setActiveItem] = useState<MagicPackItem | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const resp = await fetchMagicPackData(apiUrl ?? backpackApiPathFor(type));
    setIsLoading(false);
    const data = (resp?.data ?? resp ?? []) as MagicPackItem[];
    setItems(Array.isArray(data) ? data : []);
  }, [fetchMagicPackData, type, apiUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markItemSeen = useCallback((documentId?: string) => {
    if (!documentId) return;
    setItems((prev) =>
      prev.map((i) => (i.documentId === documentId ? { ...i, seen: true } : i)),
    );
  }, []);

  const handleItemClick = (item: MagicPackItem) => {
    setActiveItem(item);

    if (!item.documentId) return;

    // Optimistic update — mark as seen immediately so "Watch Again" shows it
    // even before the PATCH completes (mirrors mobile UX after the navigation)
    markItemSeen(item.documentId);

    // Fire-and-forget PATCH after a short delay (mirrors mobile 2s setTimeout)
    const path = isAudioView
      ? `${apiPaths.UPDATE_WALL_AUDIO_SEEN}/${item.documentId}`
      : `${apiPaths.UPDATE_WALL_VIDEO_SEEN}/${item.documentId}`;
    window.setTimeout(() => {
      updateMagicPackSeen(path);
    }, 2000);
  };

  const handleViewerClose = () => {
    setActiveItem(null);
    // Don't refetch here — the PATCH has a 2s delay so the server may still
    // show seen=false. We rely on the optimistic update set in handleItemClick;
    // the page remount on next entry will pull fresh data.
  };

  const handleBack = () => {
    trackEvent(TrackingEvents.object_close_icon_clicked, {
      from: "back pack",
    });
    navigate(-1);
  };

  // ── Render an inline viewer when an item is active
  if (activeItem) {
    const ct = activeItem.contentType?.toLowerCase();
    if (ct === "video") {
      return (
        <RenderVideoViewer
          videoPathUrl={activeItem.answerContent}
          onClose={handleViewerClose}
        />
      );
    }
    if (ct === "audio") {
      return (
        <RenderAudioViewer
          audioPathUrl={activeItem.answerContent}
          displayQuestion={activeItem.title}
          description={activeItem.description}
          onClose={handleViewerClose}
        />
      );
    }
    // Fallback for image/book — close immediately
    setActiveItem(null);
  }

  return (
    <div
      className="magic-bag"
      style={{ backgroundImage: `url(${magicBackBG})` }}
    >
      <MagicBagPackHeader
        titleText={backpackTitleFor(isAudioType ? "audio" : "video")}
        onBack={handleBack}
      />

      <WallContent
        items={items}
        isAudioView={isAudioView}
        onItemClick={handleItemClick}
        isAudioType={isAudioType}
      />

      {isLoading && (
        <div className="magic-bag__loader-overlay">
          <div className="magic-bag__loader" />
        </div>
      )}
    </div>
  );
}

export default MagicBagPack;
